import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService, { refreshAccessTokenSilently } from '../../utils/apiService';
import { getAuthToken } from '../../utils/authUtils';
import { reportSystemError } from '@/lib/systemErrors/reportSystemError';

export const createBot = createAsyncThunk(
    'createBot/createBot',
    async (botData, { rejectWithValue }) => {
        try {
            if (!getAuthToken()) {
                try {
                    await refreshAccessTokenSilently();
                } catch {
                    return rejectWithValue('No authentication token found');
                }
            }
            if (!getAuthToken()) {
                return rejectWithValue('No authentication token found');
            }

            console.log('Creating bot with data:', botData);
            
            const response = await apiService.post('/bots/create', botData);
            console.log('Bot creation response:', response.data);

            return response.data;
        } catch (error) {
            console.error('Create bot error:', error);
            const details = error?.data?.details;
            const detailText = Array.isArray(details)
                ? details.map((d) => d.message || d.path).filter(Boolean).join('; ')
                : typeof details === 'string'
                  ? details
                  : null;
            const apiError =
              typeof error?.data?.error === 'string' ? error.data.error : null;
            const message =
              detailText || apiError || error.message || 'Failed to create bot';
            const code =
              typeof error?.data?.code === 'string' ? error.data.code : null;
            // Expected user-facing gates — don't pollute system errors.
            if (
              code !== 'TOKEN_NOT_TRADABLE' &&
              code !== 'TRIAL_TOKEN_NOT_TRADABLE' &&
              code !== 'TRIAL_USER_ALREADY_USED' &&
              code !== 'TRIAL_TOKEN_ALREADY_USED'
            ) {
              reportSystemError({
                phase: 'bot',
                code: 'CREATE_BOT_FAILED',
                message,
                engine: botData?.engine || null,
                details: {
                    tokenAddress: botData?.tokenAddress || null,
                    apiCode: code,
                },
              });
            }
            return rejectWithValue(message);
        }
    }
);

const initialState = {
    bot: null,
    loading: 'idle',
    error: null,
    message: null
}

const botSlice = createSlice({
    name: 'bots',
    initialState,
    reducers: {
        resetCreateBot(state) {
            state.bot = null;
            state.loading = 'idle';
            state.error = null;
            state.message = null;
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(createBot.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
                state.bot = null;
                state.message = null;
            })
            .addCase(createBot.fulfilled, (state, action) => {
                state.loading = 'idle';
                state.bot = action.payload.bot;
                state.message = action.payload.message;
                state.error = null;
            })
            .addCase(createBot.rejected, (state, action) => {
                state.loading = 'idle';
                state.error = action.payload || action.error.message;
                state.bot = null;
                state.message = null;
            });
    }
})

export const { resetCreateBot } = botSlice.actions

export default botSlice.reducer